import type { ColorblindPalette, SettingsState } from './types.js';

interface PaletteTokens {
  primary: string;
  warning: string;
  confirm: string;
  contradiction: string;
  mournCyan: string;
}

const BASE_TOKENS: PaletteTokens = {
  primary: '#3fd0e0',
  warning: '#ffb000',
  confirm: '#3fe07a',
  contradiction: '#ff6b6b',
  mournCyan: '#9fe6f0',
};

const PALETTES: Record<Exclude<ColorblindPalette, 'off'>, PaletteTokens> = {
  deuteranopia: {
    primary: '#53b9ff',
    warning: '#ffd166',
    confirm: '#7dd35c',
    contradiction: '#ff7f4f',
    mournCyan: '#b4e7ff',
  },
  protanopia: {
    primary: '#57c7ff',
    warning: '#f7d34f',
    confirm: '#78dd7f',
    contradiction: '#ff8a8a',
    mournCyan: '#bbe7f7',
  },
  tritanopia: {
    primary: '#43d3c9',
    warning: '#ffa24d',
    confirm: '#8be05e',
    contradiction: '#ff5f7a',
    mournCyan: '#99f0d8',
  },
};

const EFFECT_KEYS = ['filmGrain', 'scanline', 'vignette'] as const;

export interface EffectLevels {
  filmGrain: number;
  scanline: number;
  vignette: number;
}

export const effectLevels = (settings: SettingsState): EffectLevels => {
  if (settings.accessibility.disableScreenFx) return zeroEffects();
  return {
    filmGrain: settings.graphics.filmGrainEnabled ? settings.graphics.filmGrainIntensity : 0,
    scanline: settings.graphics.scanlineEnabled ? settings.graphics.scanlineIntensity : 0,
    vignette: settings.display.vignetteIntensity,
  };
};

const zeroEffects = (): EffectLevels => ({
  filmGrain: 0,
  scanline: 0,
  vignette: 0,
});

export const applyRootSettings = (settings: SettingsState): void => {
  const root = document.documentElement;
  const effects = effectLevels(settings);
  const palette = blendedPalette(settings);
  applyPalette(root, palette);
  applyVars(root, settings, effects);
  root.classList.toggle('bm-reduce-motion', settings.accessibility.reduceMotion);
};

const blendedPalette = (settings: SettingsState): PaletteTokens => {
  const palette = PALETTES[settings.accessibility.colorblindPalette as Exclude<ColorblindPalette, 'off'>];
  if (!palette) return BASE_TOKENS;
  const t = clamp(settings.accessibility.colorblindIntensity, 0, 1);
  return {
    primary: mixHex(BASE_TOKENS.primary, palette.primary, t),
    warning: mixHex(BASE_TOKENS.warning, palette.warning, t),
    confirm: mixHex(BASE_TOKENS.confirm, palette.confirm, t),
    contradiction: mixHex(BASE_TOKENS.contradiction, palette.contradiction, t),
    mournCyan: mixHex(BASE_TOKENS.mournCyan, palette.mournCyan, t),
  };
};

const applyPalette = (root: HTMLElement, palette: PaletteTokens): void => {
  setColor(root, '--bm-primary', palette.primary);
  setColor(root, '--bm-warning', palette.warning);
  setColor(root, '--bm-confirm', palette.confirm);
  setColor(root, '--bm-contradiction', palette.contradiction);
  setColor(root, '--bm-mourn-cyan', palette.mournCyan);
};

const applyVars = (root: HTMLElement, settings: SettingsState, effects: EffectLevels): void => {
  const font = settings.accessibility.dyslexiaFont
    ? "'Segoe UI', 'Verdana', 'Arial', sans-serif"
    : "var(--bm-mono)";
  root.style.setProperty('--bm-ui-font', font);
  root.style.setProperty('--bm-text-scale', settings.accessibility.textScale.toFixed(2));
  root.style.setProperty('--bm-paper-opacity', settings.accessibility.cardOpacity.toFixed(2));
  root.style.setProperty('--bm-comms-scale', settings.accessibility.commsScale.toFixed(2));
  root.style.setProperty('--bm-scanline-opacity', (effects.scanline * 0.22).toFixed(3));
  root.style.setProperty('--bm-scanline-strength', effects.scanline.toFixed(2));
  for (const key of EFFECT_KEYS) root.dataset[key] = effects[key] > 0 ? 'on' : 'off';
};

const setColor = (root: HTMLElement, name: string, value: string): void => {
  root.style.setProperty(name, value);
  root.style.setProperty(`${name}-rgb`, hexToRgb(value));
};

const mixHex = (from: string, to: string, t: number): string => {
  const a = parseHex(from);
  const b = parseHex(to);
  const mix = a.map((value, index) => Math.round(value + (b[index]! - value) * t));
  return `#${mix.map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

const parseHex = (value: string): [number, number, number] => [
  Number.parseInt(value.slice(1, 3), 16),
  Number.parseInt(value.slice(3, 5), 16),
  Number.parseInt(value.slice(5, 7), 16),
];

const hexToRgb = (value: string): string => parseHex(value).join(' ');

const clamp = (value: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, value));
